import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentProps, memo, type ReactNode } from 'react';

import defaultAvatar from '@/assets/img/default-avatar.png';
import { ValorantRankIcon } from '@/features/games/valorant/components/valorantRankIcon';
import { getAgentIconUrl } from '@/features/games/valorant/utils/agents';
import { isValorantRank } from '@/features/games/valorant/utils/rank';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import {
  PremiumCornerBadge,
  RarityCard,
} from '@/shared/components/ui/rarityCard';
import type { Rarity } from '@/shared/types/rarity';
import { cn } from '@/shared/utils/cn';

const duoCardVariants = cva('relative rounded-2xl', {
  variants: {
    side: {
      left: '-rotate-16',
      right: '-ml-2 rotate-16',
    },
  },
});

const imageVariants = cva('object-cover', {
  variants: {
    size: {
      small: 'h-23 w-16',
      large: 'h-34 w-25',
    },
  },
  defaultVariants: {
    size: 'small',
  },
});

interface BaseDuoCardProps
  extends
    ComponentProps<'div'>,
    VariantProps<typeof duoCardVariants>,
    VariantProps<typeof imageVariants> {
  rarity: Rarity;
  rank: ReactNode;
  side: 'left' | 'right';
  image: string;
  name: string;
  isPremium?: boolean;
}

interface SmallDuoCardProps extends BaseDuoCardProps {
  size?: 'small';
  agentId?: never;
}

interface LargeDuoCardProps extends BaseDuoCardProps {
  size: 'large';
  agentId: string;
}

export type DuoCardProps = SmallDuoCardProps | LargeDuoCardProps;

export const DuoCard = memo((props: DuoCardProps) => {
  const {
    rarity,
    rank,
    size = 'small',
    side,
    agentId,
    name,
    image,
    isPremium,
    className,
    ...rest
  } = props;

  const showAgent = props.size === 'large' && agentId;

  return (
    <RarityCard.Outer
      {...rest}
      rarity={rarity}
      size="small"
      className={cn(duoCardVariants({ side }), className)}
    >
      {isPremium && (
        <PremiumCornerBadge rarity={rarity} placement="topLeft" compact />
      )}
      <RarityCard.Inner
        className={cn(
          'flex h-full w-full flex-col items-center gap-1 rounded-xl p-1',
          imageVariants({ size }),
        )}
      >
        <div className="container-size relative flex min-h-0 w-full flex-1 items-center justify-center">
          <div className="square-fit relative">
            <Avatar className="h-full w-full">
              <AvatarFallback>
                <img
                  src={defaultAvatar}
                  alt="Default avatar"
                  className="size-full object-cover"
                />
              </AvatarFallback>
              <AvatarImage
                src={image}
                alt={name}
                className="h-full w-full object-cover"
              />
            </Avatar>
          </div>
        </div>
        {((showAgent && agentId) || rank) && (
          <div className="flex flex-row items-center justify-center gap-2">
            {isValorantRank(rank) ? (
              <ValorantRankIcon
                rank={rank}
                className={cn(
                  size === 'large' && 'size-8',
                  size === 'small' && 'size-6',
                )}
              />
            ) : (
              rank
            )}
            {showAgent && (
              <>
                <div className="h-3 w-px bg-white/50" />
                <Avatar className="size-8">
                  <AvatarImage src={getAgentIconUrl(agentId)} />
                </Avatar>
              </>
            )}
          </div>
        )}
      </RarityCard.Inner>
    </RarityCard.Outer>
  );
});
